import {computed, createApp, ref} from "vue";
import SvgVue from 'svg-vue3';
import FeedbackBubble from "@/components/approval-center/modals/advanced-revision/FeedbackBubble.vue";

const fullFeedback = ref('');
const activeFeedbackItem = ref(null);
const feedbackItems = ref(new Map());

const feedbackItemsAsArray = computed(() => Array.from(feedbackItems.value.values()));

const hasFeedback = computed(() =>
  (
    feedbackItemsAsArray.value.length > 0 &&
    feedbackItemsAsArray.value.some((item) => item.feedback)
  ) || !!fullFeedback.value
);

const mappedFeedbackItems = computed(() => feedbackItemsAsArray.value.map(
  ({selectedText: text, feedback}) => ({text, feedback})
));

const feedbackElementActiveClass = 'tw-bg-info tw-text-white tw-rounded-sm tw-text-highlight tw-cursor-pointer tw-px-1';
const feedbackElementInactiveClass = 'tw-bg-info-100 tw-rounded-sm tw-text-highlight tw-cursor-pointer tw-px-1 tw-border-b tw-border-info';

const generateFeedbackId = () => `revision-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

// Use to update feedback item classes between active and inactive states
const setFeedbackItemClass = (feedbackItem, isActive) => {
  if (!feedbackItem?.feedbackElement) {
    return;
  }

  feedbackItem.feedbackElement.className = isActive ? feedbackElementActiveClass : feedbackElementInactiveClass;
};

const updateAllFeedbackItemClasses = () => {
  feedbackItems.value.forEach(item => {
    setFeedbackItemClass(item, activeFeedbackItem.value && item.id === activeFeedbackItem.value.id);
  });
};

const openFeedbackTooltip = (feedbackItem) => {
  activeFeedbackItem.value = feedbackItem;
  updateAllFeedbackItemClasses();
};

const closeFeedbackTooltip = () => {
  if (!activeFeedbackItem.value) {
    return;
  }

  const { id: feedbackId, feedback } = activeFeedbackItem.value;

  activeFeedbackItem.value = null;
  updateAllFeedbackItemClasses();

  if (feedback) {
    return;
  }

  // Remove the feedback item if no feedback was added
  const feedbackItem = feedbackItems.value.get(feedbackId);
  removeFeedbackItem(feedbackItem);
};

/**
 * Wraps the selection range in a span with highlight classes and creates a new feedback item to add to the map.
 * Feedback items contain their id, a reference to the element, the highlighted text (text only) and the feedback
 * text added by the user
 */
const createFeedbackItem = (range, feedback = '') => {
  const feedbackElement = document.createElement('span');
  const feedbackId = generateFeedbackId();

  feedbackElement.className = feedbackElementActiveClass;
  feedbackElement.setAttribute('id', feedbackId);
  feedbackElement.setAttribute('feedbackEl', 'true');

  // Cache the original selection contents for undo/restore
  const contents = range.extractContents();
  feedbackElement.appendChild(contents);
  range.insertNode(feedbackElement);

  // Create a container for the inline FeedbackBubble
  const bubbleContainer = document.createElement('span');
  bubbleContainer.className = 'tw-inline-block';
  // Insert bubbleContainer immediately after feedbackElement
  if (feedbackElement.nextSibling) {
    feedbackElement.parentNode.insertBefore(bubbleContainer, feedbackElement.nextSibling);
  } else {
    feedbackElement.parentNode.appendChild(bubbleContainer);
  }

  const granularFeedbackItem = {
    id: feedbackId,
    selectedText: feedbackElement.textContent,
    feedback,
    feedbackElement,
    bubbleContainer,
  };

  feedbackItems.value.set(feedbackId, granularFeedbackItem);
  addElementEventListener(feedbackId);

  const app = createApp(FeedbackBubble, {
    feedbackItem: granularFeedbackItem,
    onClick: () => openFeedbackTooltip(granularFeedbackItem),
  });
  app.use(SvgVue);
  app.mount(bubbleContainer);

  return granularFeedbackItem;
};

const updateFeedback = (feedback) => {
  const feedbackItem = feedbackItems.value.get(activeFeedbackItem.value.id);

  if (!activeFeedbackItem.value || !feedbackItem) {
    closeFeedbackTooltip();
  }

  feedbackItem.feedback = feedback;
  feedbackItems.value.set(activeFeedbackItem.value.id, feedbackItem);

  closeFeedbackTooltip();
};

const deleteFeedback = () => {
  if (!activeFeedbackItem.value) {
    return;
  }

  const feedbackItem = feedbackItems.value.get(activeFeedbackItem.value.id);

  removeFeedbackItem(feedbackItem);
  closeFeedbackTooltip();
};

const removeFeedbackItem = (feedbackItem) => {
  if (!feedbackItem?.feedbackElement?.parentNode) {
    return;
  }

  const parent = feedbackItem.feedbackElement.parentNode;

  // Remove the feedbackElement and replace with its text
  parent.replaceChild(document.createTextNode(feedbackItem.selectedText), feedbackItem.feedbackElement);

  // Remove the bubbleContainer if it exists
  if (feedbackItem.bubbleContainer && feedbackItem.bubbleContainer.parentNode) {
    feedbackItem.bubbleContainer.parentNode.removeChild(feedbackItem.bubbleContainer);
  }

  feedbackItems.value.delete(feedbackItem.id);
};

// Add event listener to feedback elements to reopen the feedback tooltip when clicked
const addElementEventListener = (feedbackId) => {
  const feedbackItem = feedbackItems.value.get(feedbackId);
  if (!feedbackItem?.feedbackElement) {
    return;
  }

  const { feedbackElement } = feedbackItem;
  // Remove any existing click listener to avoid duplicates
  feedbackElement.onclick = null;
  feedbackElement.addEventListener('click', (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (document.getElementById(feedbackId)) {
      openFeedbackTooltip(feedbackItem);
    }
  });
};

export default () => ({
  fullFeedback,
  activeFeedbackItem,
  feedbackItems,
  feedbackItemsAsArray,
  hasFeedback,
  mappedFeedbackItems,
  openFeedbackTooltip,
  closeFeedbackTooltip,
  createFeedbackItem,
  updateAllFeedbackItemClasses,
  updateFeedback,
  deleteFeedback,
  removeFeedbackItem,
});
